import type { IRoutingGraph } from "@multiconsult-group/multiplacer-core";
import type { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";

export type RoutingGraphSchemaPixGeometry = {
  pixPolygons: IPixPolygon[];
  pixPaths: IPixPath[];
};

export const A1_LANDSCAPE_WIDTH_M = 0.841;
export const A1_LANDSCAPE_HEIGHT_M = 0.594;
const A1_FRAME_MARGIN_M = 0.02;
const A1_ZONE_BAND_M = 0.012;
const A1_TITLE_BLOCK_WIDTH_M = 0.26;
const A1_TITLE_BLOCK_HEIGHT_M = 0.07;
const ROOM_POLYGON_WIDTH_M = 0.08;
const ROOM_POLYGON_HEIGHT_M = 0.045;
const ROOM_PLACEMENT_PADDING_M = 0.02;
const DRAWING_STROKE_COLOR = "#1f2933";
const DRAWING_STROKE_WIDTH = "0.045";
const DRAWING_LIGHT_STROKE_WIDTH = "0.025";

const emptyText = {
  values: [],
  color: undefined,
  fontSize: undefined,
};

const buildRectPoints = (left: number, top: number, width: number, height: number): number[][] => [
  [left, top],
  [left + width, top],
  [left + width, top + height],
  [left, top + height],
  [left, top],
];

const createPath = (id: string, points: number[][], tooltip?: string): IPixPath => ({
  id,
  points,
  strokeColor: DRAWING_STROKE_COLOR,
  strokeWidth: DRAWING_STROKE_WIDTH,
  tooltip,
});

const createTextPolygon = (
  id: string,
  text: string,
  left: number,
  top: number,
  width: number,
  height: number,
  fontSize = "0.65"
): IPixPolygon => ({
  id,
  entityId: id,
  points: buildRectPoints(left, top, width, height),
  fillColor: undefined,
  strokeColor: undefined,
  strokeWidth: undefined,
  text: {
    show: true,
    values: [text],
    color: DRAWING_STROKE_COLOR,
    fontSize,
  },
  tooltip: text,
  isNotClickable: true,
  isMoveable: false,
  isDragSelectable: false,
  zIndex: 20,
});

export const buildA1SchemaSheetPixGeometry = (): RoutingGraphSchemaPixGeometry => {
  const width = A1_LANDSCAPE_WIDTH_M;
  const height = A1_LANDSCAPE_HEIGHT_M;
  const margin = A1_FRAME_MARGIN_M;
  const titleBlockLeft = width - margin - A1_TITLE_BLOCK_WIDTH_M;
  const titleBlockTop = height - margin - A1_TITLE_BLOCK_HEIGHT_M;
  const innerLeft = margin + A1_ZONE_BAND_M;
  const innerTop = margin + A1_ZONE_BAND_M;
  const innerRight = width - margin - A1_ZONE_BAND_M;
  const innerBottom = height - margin - A1_ZONE_BAND_M;
  const titleRowHeight = A1_TITLE_BLOCK_HEIGHT_M / 4;
  const titleCol1 = titleBlockLeft + 0.065;
  const titleCol2 = titleBlockLeft + 0.16;
  const zoneLabelsX = ["1", "2", "3", "4", "5", "6", "7", "8"];
  const zoneLabelsY = ["A", "B", "C", "D"];

  return {
    pixPolygons: [
      {
        id: "routing-schema-a1-sheet",
        entityId: "routing-schema-a1-sheet",
        points: [
          [0, 0],
          [width, 0],
          [width, height],
          [0, height],
        ],
        fillColor: "#ffffff",
        strokeColor: DRAWING_STROKE_COLOR,
        strokeWidth: DRAWING_STROKE_WIDTH,
        text: emptyText,
        tooltip: "A1",
        isNotClickable: true,
        isMoveable: false,
        isDragSelectable: false,
      },
      createTextPolygon("routing-schema-a1-label", "A1", 0.01, 0.01, 0.025, 0.012, "0.45"),
      createTextPolygon(
        "routing-schema-drawing-title",
        "Flytschhema sprinkler",
        innerLeft + 0.004,
        innerTop + 0.004,
        0.22,
        0.028,
        "1.35"
      ),
      createTextPolygon(
        "routing-schema-title-main",
        "FLYTSKJEMA",
        titleBlockLeft + 0.004,
        titleBlockTop + titleRowHeight,
        titleCol2 - titleBlockLeft - 0.008,
        titleRowHeight * 2,
        "0.9"
      ),
      createTextPolygon(
        "routing-schema-title-project-label",
        "PROSJEKT",
        titleCol2 + 0.004,
        titleBlockTop + 0.002,
        width - margin - titleCol2 - 0.008,
        titleRowHeight,
        "0.42"
      ),
      createTextPolygon(
        "routing-schema-title-floor-label",
        "ETASJE",
        titleCol2 + 0.004,
        titleBlockTop + titleRowHeight + 0.002,
        width - margin - titleCol2 - 0.008,
        titleRowHeight,
        "0.42"
      ),
      createTextPolygon(
        "routing-schema-title-scale",
        "MÅL 1:100",
        titleBlockLeft + 0.004,
        titleBlockTop + titleRowHeight * 3,
        titleCol1 - titleBlockLeft - 0.008,
        titleRowHeight,
        "0.45"
      ),
      createTextPolygon(
        "routing-schema-title-date",
        "DATO",
        titleCol1 + 0.004,
        titleBlockTop + titleRowHeight * 3,
        titleCol2 - titleCol1 - 0.008,
        titleRowHeight,
        "0.42"
      ),
      createTextPolygon(
        "routing-schema-title-drawing-no",
        "TEGNINGSNR.",
        titleCol2 + 0.004,
        titleBlockTop + titleRowHeight * 2 + 0.002,
        width - margin - titleCol2 - 0.008,
        titleRowHeight,
        "0.42"
      ),
      ...zoneLabelsX.flatMap((label, index) => {
        const x = innerLeft + ((innerRight - innerLeft) * (index + 0.5)) / zoneLabelsX.length;
        return [
          createTextPolygon(
            `routing-schema-zone-top-${label}`,
            label,
            x - 0.006,
            margin + 0.001,
            0.012,
            A1_ZONE_BAND_M - 0.002,
            "0.38"
          ),
          createTextPolygon(
            `routing-schema-zone-bottom-${label}`,
            label,
            x - 0.006,
            height - margin - A1_ZONE_BAND_M + 0.001,
            0.012,
            A1_ZONE_BAND_M - 0.002,
            "0.38"
          ),
        ];
      }),
      ...zoneLabelsY.flatMap((label, index) => {
        const y = innerTop + ((innerBottom - innerTop) * (index + 0.5)) / zoneLabelsY.length;
        return [
          createTextPolygon(
            `routing-schema-zone-left-${label}`,
            label,
            margin + 0.001,
            y - 0.006,
            A1_ZONE_BAND_M - 0.002,
            0.012,
            "0.38"
          ),
          createTextPolygon(
            `routing-schema-zone-right-${label}`,
            label,
            width - margin - A1_ZONE_BAND_M + 0.001,
            y - 0.006,
            A1_ZONE_BAND_M - 0.002,
            0.012,
            "0.38"
          ),
        ];
      }),
    ],
    pixPaths: [
      createPath(
        "routing-schema-a1-border",
        buildRectPoints(margin, margin, width - margin * 2, height - margin * 2),
        "A1 ytterramme"
      ),
      {
        ...createPath(
          "routing-schema-a1-inner-frame",
          buildRectPoints(innerLeft, innerTop, innerRight - innerLeft, innerBottom - innerTop),
          "A1 tegningsramme"
        ),
        strokeWidth: DRAWING_LIGHT_STROKE_WIDTH,
      },
      createPath(
        "routing-schema-a1-title-block",
        buildRectPoints(
          titleBlockLeft,
          titleBlockTop,
          width - margin - titleBlockLeft,
          height - margin - titleBlockTop
        ),
        "Tittelfelt"
      ),
      createPath("routing-schema-title-row-1", [
        [titleBlockLeft, titleBlockTop + titleRowHeight],
        [width - margin, titleBlockTop + titleRowHeight],
      ]),
      createPath("routing-schema-title-row-2", [
        [titleBlockLeft, titleBlockTop + titleRowHeight * 2],
        [width - margin, titleBlockTop + titleRowHeight * 2],
      ]),
      createPath("routing-schema-title-row-3", [
        [titleBlockLeft, titleBlockTop + titleRowHeight * 3],
        [width - margin, titleBlockTop + titleRowHeight * 3],
      ]),
      createPath("routing-schema-title-col-1", [
        [titleCol1, titleBlockTop + titleRowHeight * 3],
        [titleCol1, height - margin],
      ]),
      createPath("routing-schema-title-col-2", [
        [titleCol2, titleBlockTop],
        [titleCol2, height - margin],
      ]),
      ...zoneLabelsX.slice(1).flatMap((_, index) => {
        const x = innerLeft + ((innerRight - innerLeft) * (index + 1)) / zoneLabelsX.length;
        return [
          {
            ...createPath(`routing-schema-zone-x-top-${index + 1}`, [
              [x, margin],
              [x, innerTop],
            ]),
            strokeWidth: DRAWING_LIGHT_STROKE_WIDTH,
          },
          {
            ...createPath(`routing-schema-zone-x-bottom-${index + 1}`, [
              [x, innerBottom],
              [x, height - margin],
            ]),
            strokeWidth: DRAWING_LIGHT_STROKE_WIDTH,
          },
        ];
      }),
      ...zoneLabelsY.slice(1).flatMap((_, index) => {
        const y = innerTop + ((innerBottom - innerTop) * (index + 1)) / zoneLabelsY.length;
        return [
          {
            ...createPath(`routing-schema-zone-y-left-${index + 1}`, [
              [margin, y],
              [innerLeft, y],
            ]),
            strokeWidth: DRAWING_LIGHT_STROKE_WIDTH,
          },
          {
            ...createPath(`routing-schema-zone-y-right-${index + 1}`, [
              [innerRight, y],
              [width - margin, y],
            ]),
            strokeWidth: DRAWING_LIGHT_STROKE_WIDTH,
          },
        ];
      }),
    ],
  };
};

const getRoutingGraphRoomNames = (routingGraph: IRoutingGraph): string[] => {
  const rooms = routingGraph.nodes.flatMap((node) => node.rooms ?? []);

  if (rooms.length === 0 && routingGraph.roomNumber) {
    rooms.push(routingGraph.roomNumber);
  }

  return Array.from(new Set(rooms.map((room) => room.trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
};

const seededUnitValue = (seed: string, salt: string): number => {
  let hash = 2166136261;
  const text = `${seed}:${salt}`;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0) / 4294967295;
};

const createRoomSchemaPolygon = (roomName: string, index: number): IPixPolygon => {
  const minX = A1_FRAME_MARGIN_M + ROOM_PLACEMENT_PADDING_M;
  const minY = A1_FRAME_MARGIN_M + ROOM_PLACEMENT_PADDING_M;
  const maxX =
    A1_LANDSCAPE_WIDTH_M - A1_FRAME_MARGIN_M - ROOM_PLACEMENT_PADDING_M - ROOM_POLYGON_WIDTH_M;
  const maxY =
    A1_LANDSCAPE_HEIGHT_M -
    A1_FRAME_MARGIN_M -
    ROOM_PLACEMENT_PADDING_M -
    A1_TITLE_BLOCK_HEIGHT_M -
    ROOM_POLYGON_HEIGHT_M;
  const x = minX + seededUnitValue(roomName, "x") * Math.max(0, maxX - minX);
  const y = minY + seededUnitValue(roomName, "y") * Math.max(0, maxY - minY);

  return {
    id: `routing-schema-room-${index}-${roomName}`,
    entityId: roomName,
    points: [
      [x, y],
      [x + ROOM_POLYGON_WIDTH_M, y],
      [x + ROOM_POLYGON_WIDTH_M, y + ROOM_POLYGON_HEIGHT_M],
      [x, y + ROOM_POLYGON_HEIGHT_M],
      [x, y],
    ],
    fillColor: "#edf6ff",
    strokeColor: "#1f6feb",
    strokeWidth: "0.08",
    text: {
      show: true,
      values: [roomName],
      color: "#0b2f5b",
      fontSize: "0.8",
    },
    tooltip: roomName,
    isNotClickable: true,
    isMoveable: false,
    isDragSelectable: false,
    zIndex: 10,
  };
};

export const buildRoutingGraphRoomSchemaPixGeometry = (
  routingGraph: IRoutingGraph
): RoutingGraphSchemaPixGeometry => {
  const roomNames = getRoutingGraphRoomNames(routingGraph);

  return {
    pixPolygons: roomNames.map(createRoomSchemaPolygon),
    pixPaths: [],
  };
};

export const routingGraphToSchemaPixGeometry = (
  routingGraph: IRoutingGraph
): RoutingGraphSchemaPixGeometry => {
  const a1Sheet = buildA1SchemaSheetPixGeometry();
  const rooms = buildRoutingGraphRoomSchemaPixGeometry(routingGraph);

  return {
    pixPolygons: [...a1Sheet.pixPolygons, ...rooms.pixPolygons],
    pixPaths: [...a1Sheet.pixPaths, ...rooms.pixPaths],
  };
};
